import { createFileRoute } from '@tanstack/react-router';
import { Alert, Badge, Button, Card, Group, Loader, Modal, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { IconInfoCircle, IconLink, IconPlug, IconPlus, IconTrash } from '@tabler/icons-react';
import { EmptyState } from '@/shared/components/EmptyState';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { useCurrentUserId } from '@/shared/hooks/useCurrentUserId';
import { useCrudResource } from '@/shared/hooks/useCrudResource';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { Paginated } from '@/shared/components/DataTable/TablePagination';
import { CalendarConnectionCard } from '@/features/planning/CalendarConnectionCard';
import { McpConnectCard } from '@/features/mcp/McpConnectCard';

export const Route = createFileRoute('/_app/settings/integrations')({ component: IntegrationsPage });

// BE v1.9: accessToken/refreshToken non sono più serializzati nelle risposte
// (write-only); in lettura arrivano solo i metadati dell'integrazione.
interface TokenRow extends Denormalized {
  type: 'third-parts-tokens';
  id: string;
  email?: string;
  accessType?: string;
  otpId?: string;
  user?: { id: string };
  [k: string]: unknown;
}

const PROVIDERS: Array<{ value: string; label: string; description: string; icon?: string }> = [
  { value: 'google', label: 'Google', description: 'Calendar / Drive / Gmail' },
  { value: 'microsoft', label: 'Microsoft', description: 'Outlook / OneDrive' },
  { value: 'slack', label: 'Slack', description: 'Notifiche workspace' },
  { value: 'github', label: 'GitHub', description: 'Issues / PR' },
  { value: 'jira', label: 'Jira', description: 'Issue tracking' },
  { value: 'custom', label: 'Custom', description: 'Webhook / API custom' },
];

function IntegrationsPage() {
  const userId = useCurrentUserId();
  const [opened, { open, close }] = useDisclosure(false);
  const [form, setForm] = useState({ accessType: 'google', email: '', accessToken: '', refreshToken: '' });

  const crud = useCrudResource<TokenRow>({
    resource: 'third-parts-tokens',
    queryKey: ['my-integrations', userId],
    enabled: Boolean(userId),
    list: { filter: { 'user.id': { eq: userId } }, page: { size: 100 } },
    labels: { create: 'Integrazione collegata', remove: 'Integrazione rimossa' },
    onCreateSuccess: () => {
      setForm({ accessType: 'google', email: '', accessToken: '', refreshToken: '' });
      close();
    },
  });
  const items = crud.items;

  const handleCreate = () => {
    crud.create.mutate({
      attributes: {
        accessType: form.accessType,
        email: form.email,
        accessToken: form.accessToken,
        refreshToken: form.refreshToken,
      },
      relationships: { user: { data: { type: 'users', id: userId } } },
    });
  };

  return (
    <Stack maw={920}>
      <Group justify="space-between">
        <Group>
          <IconPlug size={28} />
          <Title order={2}>Integrazioni esterne</Title>
        </Group>
        <Button leftSection={<IconPlus size={14} />} onClick={open}>Collega integrazione</Button>
      </Group>

      <McpConnectCard />

      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Gestisce i token di accesso per servizi esterni (Google, Microsoft, ecc.). Il flusso OAuth automatizzato e in arrivo: per ora inserisci manualmente i token o lascia che il sistema li popoli al primo handshake.
      </Alert>

      {/* Il calendario personale è una cosa diversa dai token qui sotto: quelli sono
          credenziali di servizio inserite a mano, questo è il consenso di una persona sul
          proprio account. Tenerli nella stessa tabella confonderebbe due permessi diversi. */}
      <CalendarConnectionCard />

      <Card withBorder padding={0} radius="lg">
        {crud.isLoading ? <Loader m="md" /> : items.length === 0 ? (
          <EmptyState
            icon={IconPlug}
            color="indigo"
            title="Nessuna integrazione collegata"
            description="Collega Google, Slack o un webhook custom per ricevere notifiche e sincronizzare dati."
          />
        ) : (
          <Paginated rows={items} label="integrazioni">
          {(pageRows) => (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Account</Table.Th>
                <Table.Th style={{ width: 100 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((t) => {
                const provider = PROVIDERS.find((p) => p.value === t.accessType) ?? PROVIDERS[5];
                return (
                  <Table.Tr key={t.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <Badge variant="filled" color="blue" leftSection={<IconLink size={12} />}>
                          {provider.label}
                        </Badge>
                        <Text size="xs" c="dimmed">{provider.description}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>{t.email ?? '—'}</Table.Td>
                    <Table.Td>
                      <Button size="xs" variant="subtle" color="red" leftSection={<IconTrash size={14} />} onClick={() => crud.remove.mutate(t.id)}>
                        Rimuovi
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          )}
          </Paginated>
        )}
      </Card>

      <Modal opened={opened} onClose={close} title="Nuova integrazione" size="lg">
        <Stack>
          <Select
            label="Provider"
            value={form.accessType}
            onChange={(v) => setForm((f) => ({ ...f, accessType: v ?? 'google' }))}
            data={PROVIDERS.map((p) => ({ value: p.value, label: p.label }))}
          />
          <TextInput label="Email account" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.currentTarget.value }))} />
          <TextInput label="Access Token" value={form.accessToken} onChange={(e) => setForm((f) => ({ ...f, accessToken: e.currentTarget.value }))} placeholder="ya29.a0Af..." />
          <TextInput label="Refresh Token (opzionale)" value={form.refreshToken} onChange={(e) => setForm((f) => ({ ...f, refreshToken: e.currentTarget.value }))} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>Annulla</Button>
            <Button leftSection={<IconLink size={14} />} loading={crud.create.isPending} disabled={!form.email || !form.accessToken} onClick={handleCreate}>
              Collega
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
